import { useState, useCallback, useRef, useEffect } from "react"
import { ContactMethod, Step, UseHelpDrawerReturn } from "./types"
import { validateCurrentStep } from "./utils"
import { MIN_DESCRIPTION_LENGTH, COPY } from "./constants"
import { useAuthContext } from "../../AuthContextWrapper"
import { formatPhone } from "../../utils/formatPhone"
import { ContactUsAPI } from "../../apis/ContactUs"

export const useHelpDrawer = (onClose: () => void): UseHelpDrawerReturn => {
  const [description, setDescription] = useState("")
  const [uploadedFiles, setUploadedFiles] = useState<FileList | undefined>()
  const [currentStep, setCurrentStep] = useState(Step.DESCRIPTION)
  const [isDescriptionValid, setIsDescriptionValid] = useState(true)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [contactPreferences, setContactPreferences] = useState<ContactMethod>(ContactMethod.PHONE)
  const { user, estates, countries, states } = useAuthContext()
  const existingPhoneNumber = formatPhone(user?.phone || "")
  const existingEmail = user?.email || ""

  const [contactInfo, setContactInfo] = useState("")
  const [isContactInfoValid, setIsContactInfoValid] = useState<boolean | null>(null)
  const resetTimeout = useRef<NodeJS.Timeout | null>(null)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [fileUploadErrors, setFileUploadErrors] = useState<string[]>([])
  const [showContactInfoForm, setShowContactInfoForm] = useState(false)
  const [isSendingFeedback, setIsSendingFeedback] = useState(false)
  const { sendFeedback } = new ContactUsAPI()

  const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value)
  }, [])

  const validateStep = useCallback(() => {
    const validationResult = validateCurrentStep(
      currentStep,
      description,
      fileUploadErrors,
      isContactInfoValid,
      contactPreferences,
      showContactInfoForm,
      MIN_DESCRIPTION_LENGTH
    )

    setIsDescriptionValid(
      currentStep === Step.DESCRIPTION ? validationResult.isValid : isDescriptionValid
    )
    setValidationError(validationResult.errorMessage)

    return validationResult.isValid
  }, [
    currentStep,
    description,
    fileUploadErrors,
    isContactInfoValid,
    contactPreferences,
    showContactInfoForm,
    isDescriptionValid,
  ])

  const uploadAndSendFeedback = useCallback(async () => {
    try {
      if (!user?.id) {
        setValidationError("We were unable to submit your request, if the problem persists please log out and back in")
        return
      }

      const uploadFile = uploadedFiles?.item(0) || undefined
      const contactMethod = contactPreferences === ContactMethod.PHONE ? "Phone call" : "Email"
      const contactDetails = contactInfo
      const estateCountry = countries?.find((c) => c.id === estates?.[0]?.countryId)
      const estateState = states?.find((s) => s.id === estates?.[0]?.stateId)

      setIsSendingFeedback(true)

      const content = COPY.SUPPORT_REQUEST.TEMPLATE.replace("{description}", description)
        .replace("{contactMethod}", contactMethod)
        .replace("{contactDetails}", contactDetails)
        .replace("{userFullName}", user?.fullName || "")
        .replace("{estateName}", estates?.[0]?.name || "")
        .replace("{estateId}", String(estates?.[0]?.id))
        .replace("{estateCountry}", estateCountry?.fullName || "")
        .replace("{estateState}", estateState?.fullName || "")
        .replace("{fileInfo}", uploadFile ? `File attached: ${uploadFile.name}` : "")
        .trim()

      const response = await sendFeedback(user.id, content, uploadFile)

      if (response) {
        setIsSendingFeedback(false)
        setCurrentStep(Step.CONFIRMATION)
      } else {
        setIsSendingFeedback(false)
        throw new Error("No response from feedback API")
      }
    } catch (error) {
      console.error("Error submitting feedback:", error)
      setValidationError("There was an error submitting your request. Please try again.")
      setIsSendingFeedback(false)
    }
  }, [
    uploadedFiles,
    contactPreferences,
    contactInfo,
    description,
    sendFeedback,
    countries,
    states,
    estates,
    user,
  ])

  const handleSubmit = useCallback(() => {
    if (!validateStep()) return
    setValidationError(null)
    uploadAndSendFeedback()
  }, [validateStep, uploadAndSendFeedback])

  const handleNextStep = useCallback(() => {
    if (!validateStep()) return
    setCurrentStep(currentStep + 1)
  }, [currentStep, validateStep])

  const handleBackStep = useCallback(() => {
    setValidationError(null)
    if (showContactInfoForm) {
      setShowContactInfoForm(false)
    } else {
      setCurrentStep(currentStep - 1)
    }
  }, [currentStep, showContactInfoForm])

  const closeAndReset = useCallback(() => {
    const currentPath = window.location.pathname

    if (currentPath.includes("help")) {
      window.location.href = "/app/home"
    } else {
      onClose()
    }

    if (resetTimeout.current) {
      clearTimeout(resetTimeout.current)
    }

    resetTimeout.current = setTimeout(() => {
      setCurrentStep(Step.DESCRIPTION)
      setDescription("")
      setUploadedFiles(undefined)
      setContactPreferences(ContactMethod.PHONE)
      setContactInfo("")
      setIsContactInfoValid(null)
      setShowContactInfoForm(false)
      setShowCloseConfirmation(false)
    }, 1000)
  }, [onClose])

  const handleCloseAttempt = useCallback(() => {
    const hasChanges =
      description.trim() !== "" ||
      uploadedFiles !== undefined ||
      contactInfo.trim() !== "" ||
      showContactInfoForm

    if (hasChanges && !showCloseConfirmation && currentStep !== Step.CONFIRMATION) {
      setShowCloseConfirmation(true)
    } else {
      closeAndReset()
    }
  }, [
    description,
    uploadedFiles,
    contactInfo,
    closeAndReset,
    currentStep,
    showContactInfoForm,
    showCloseConfirmation,
  ])

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirmation(false)
  }, [])

  const handleContactInfoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (contactPreferences === ContactMethod.PHONE) {
        setContactInfo(formatPhone(event.target.value))
      } else {
        setContactInfo(event.target.value)
      }
    },
    [contactPreferences]
  )

  const handleConfirmClose = useCallback(() => {
    closeAndReset()
  }, [closeAndReset])

  const resetValidationError = useCallback(() => {
    setValidationError(null)
    setIsContactInfoValid(null)
    setShowCloseConfirmation(false)
  }, [])

  useEffect(() => {
    if (
      existingPhoneNumber &&
      contactPreferences === ContactMethod.PHONE &&
      currentStep === Step.CONTACT_PREFERENCES
    ) {
      setContactInfo(existingPhoneNumber)
    }
  }, [existingPhoneNumber, contactPreferences, currentStep])

  useEffect(() => {
    if (
      existingEmail &&
      contactPreferences === ContactMethod.EMAIL &&
      currentStep === Step.CONTACT_PREFERENCES
    ) {
      setContactInfo(existingEmail)
    }
  }, [existingEmail, contactPreferences, currentStep])

  return {
    state: {
      description,
      uploadedFiles,
      currentStep,
      isDescriptionValid,
      validationError,
      contactPreferences,
      existingPhoneNumber,
      existingEmail,
      contactInfo,
      isContactInfoValid,
      showCloseConfirmation,
      fileUploadErrors,
      showContactInfoForm,
      isSendingFeedback,
    },
    handlers: {
      handleContactInfoChange,
      handleDescriptionChange,
      handleSubmit,
      handleNextStep,
      handleBackStep,
      handleCloseAttempt,
      handleCancelClose,
      handleConfirmClose,
      resetValidationError,
      setUploadedFiles,
      setFileUploadErrors,
      setContactPreferences,
      setContactInfo,
      setIsContactInfoValid,
      setShowContactInfoForm,
      closeAndReset,
    },
  }
}
